import { useCallback, useState, useRef, useEffect } from 'react';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import { ActionMenu, ActionMenuSection, useActionMenu } from 'components/ActionMenu';
import { AccessRolesStructureType } from '@constants';
import { useAccessRoles } from 'hooks/useAccessRoles';
import { useGrowthBookFlag } from 'hooks/useGrowthBookFlag';
import {
  OpenSetSignInStatusModalArgs,
  buildSignInStatusMenuItem,
  useSetSignInStatusModal,
} from 'TeamModule/hooks/useSetSignInStatusModal';
import { MemberSearchItem } from './types';
import type { MemberImpersonationItem } from 'TeamModule/api/membersImpersonationApi';
import { useDispatch, useSelector } from 'react-redux';
import { resetMemberPassword } from 'thunk';
import SimpleModal from 'components/SimpleModal';
import useSetUsersTemporaryPasswordModal from 'TeamModule/components/SetUsersTemporaryPasswordModal/useSetUsersTemporaryPasswordModal';
import { bulkUpdateTeamMembersAccessLevel as bulkUpdateTeamMembersAccessLevelApi } from 'TeamModule/services/BulkUpdateTeamMembersAccessLevelApi';
import { updateAccountAttributes as updateAccountAttributesApi } from 'TeamModule/services/UpdateAccountCsmAttributeApi';
import { bulkToggleMfaReset } from 'TeamModule/services/BulkToggleMfaResetApi';
import {
  buildChelloTierMenuItem,
  useSetChelloTierModal,
} from 'TeamModule/hooks/useSetChelloTierModal';
import * as ProfileServices from 'ProfileModule/services';
import ApiClientManager from 'core/apiClient';
import { selectAuthToken } from 'selectors';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { useSwitchTenantMutation } from 'TenantModule/api/queries/useSwitchTenant';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

/**
 * GLX-2012: "Set Sign-in Status", for one organization at a time with every member resolved (the same
 * gate as Bulk Set Temporary Password). Empty while memberManagementTableV2Flag is off.
 */
const buildSignInStatusItems = ({
  isOn,
  isReady,
  groupKey,
  groupMembers,
  selectionSize,
  impersonationMap,
  batchLimit,
  openModal,
}: {
  isOn: boolean;
  isReady: boolean;
  groupKey: string | undefined;
  groupMembers: MemberSearchItem[] | undefined;
  /** Every selected row, including any whose organization couldn't be parsed. */
  selectionSize: number;
  impersonationMap: Map<string, MemberImpersonationItem>;
  batchLimit: number;
  openModal: (args: OpenSetSignInStatusModalArgs) => void;
}) => {
  if (!isOn) return [];

  const members = (groupMembers ?? []).flatMap((m) => {
    const teamMembershipId = impersonationMap.get(m.id)?.teamMembershipId;
    if (!teamMembershipId) return [];
    return [
      { teamMembershipId, name: [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email },
    ];
  });
  const [tenant, realmId, teamIdText] = (groupKey ?? '').split('|');
  const teamId = parseInt(teamIdText, 10);

  return [
    buildSignInStatusMenuItem({
      // members drops unresolved rows, so it must still cover the whole selection.
      enabled:
        isReady && teamId > 0 && members.length === selectionSize && members.length <= batchLimit,
      onSelect: (loginEnabled) => openModal({ tenant, realmId, teamId, loginEnabled, members }),
    }),
  ];
};

export const useMembersV2BulkActionMenu = () => {
  const { accessRolesStructure } = useAccessRoles();
  const { openModal } = useDynamicModal();
  const { anchorEl, anchorPosition, isOpen, openMenu, closeMenu } = useActionMenu();
  const [menuSections, setMenuSections] = useState<ActionMenuSection[]>([]);
  // Track pending menu open to avoid flushSync - menu opens after sections are set
  const pendingOpenRef = useRef<HTMLElement | null>(null);

  // Open menu after sections are set (avoids flushSync anti-pattern)
  useEffect(() => {
    if (pendingOpenRef.current && menuSections.length > 0) {
      openMenu(pendingOpenRef.current);
      pendingOpenRef.current = null;
    }
  }, [menuSections, openMenu]);
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);
  const { openSetUsersTemporaryPasswordModal } = useSetUsersTemporaryPasswordModal();
  const { openSetChelloTierModal } = useSetChelloTierModal();
  const { openSetSignInStatusModal, batchLimit: signInStatusBatchLimit } =
    useSetSignInStatusModal();
  const { isOn: isSignInStatusOn } = useGrowthBookFlag('memberManagementTableV2Flag');
  const { showSuccess, showError } = useNotification();
  const { getSandbox, handleSelectGlobalRailsSandbox } = useSandboxes();
  const { tenantOrder, tenantsHash, handleSelectGlobalTenant } = useTenants({ isOff: false });
  const { switchTenant } = useSwitchTenantMutation();

  const openMembersV2BulkActionMenu = useCallback(
    ({
      target,
      members,
      impersonationMap,
    }: {
      target: HTMLElement;
      members: MemberSearchItem[];
      impersonationMap: Map<string, MemberImpersonationItem>;
    }) => {
      if (!target) return;

      const groups = members.reduce(
        (acc, m) => {
          const teamId = parseInt(String(m.teamId), 10);
          if (Number.isNaN(teamId)) return acc;
          const key = `${m.tenant}|${m.realmId}|${teamId}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(m);
          return acc;
        },
        {} as Record<string, MemberSearchItem[]>,
      );

      const groupKeys = Object.keys(groups);
      const isSingleTeam = groupKeys.length === 1;

      const emails = members.map((m) => m.email).filter(Boolean);

      const accountIds = members
        .map((m) => impersonationMap.get(m.id)?.accountId)
        .filter(Boolean) as number[];

      const teamIdForSingle = isSingleTeam ? parseInt(groupKeys[0].split('|')[2] || '', 10) : 0;

      const membershipIdsForSingleTeam = isSingleTeam
        ? (groups[groupKeys[0]]
            .map((m) => impersonationMap.get(m.id)?.teamMembershipId)
            .filter(Boolean) as number[])
        : [];

      const allMembershipIdsAvailable =
        isSingleTeam && membershipIdsForSingleTeam.length === groups[groupKeys[0]].length;
      const allAccountIdsAvailableForSingle =
        isSingleTeam && accountIds.length === groups[groupKeys[0]].length;

      const onModifyRoleOption = ({
        label,
        subRoles,
        roleId,
        shortLabel,
      }: AccessRolesStructureType): any => {
        const hasSubItems = subRoles && subRoles.length > 0;
        // Parent items (with subRoles) must remain enabled so users can hover to open submenus.
        // Only leaf items (actual role options with roleId) should be disabled when conditions aren't met.
        // Conditions for leaf items: must be single team, have a roleId, and have membership IDs cached.
        const isDisabled = hasSubItems
          ? false
          : !isSingleTeam || !roleId || !allMembershipIdsAvailable;

        return {
          label: shortLabel ?? label,
          subItems: subRoles?.map(onModifyRoleOption),
          onClick:
            isSingleTeam && roleId && allMembershipIdsAvailable
              ? async () => {
                  if (!token) return;
                  const [tenant, realmId] = groupKeys[0].split('|');
                  const headers = ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId });
                  try {
                    await bulkUpdateTeamMembersAccessLevelApi({
                      params: {
                        team_id: teamIdForSingle,
                        team_membership_ids: membershipIdsForSingleTeam,
                        team_role: roleId,
                      },
                      token,
                      config: { headers },
                    });
                    showSuccess('Update member role successful!');
                  } catch (e) {
                    showError(`Update failed: ${extractApiErrorMessage(e)}`);
                  }
                }
              : undefined,
          disabled: isDisabled,
        };
      };

      const sections: ActionMenuSection[] = [
        {
          title: 'Password Management',
          items: [
            {
              label: 'Request Password Reset',
              onClick: emails.length
                ? () =>
                    openModal({
                      modal: ({ toggle }) => (
                        <SimpleModal
                          open
                          onClose={toggle}
                          onConfirm={() => dispatch(resetMemberPassword({ emails }) as any)}
                          closeOnConfirm
                          Body={<div>Send password reset to {emails.length} users?</div>}
                        />
                      ),
                    })
                : undefined,
              disabled: emails.length === 0,
            },
            {
              label: 'Bulk Set Temporary Password',
              onClick:
                isSingleTeam && allAccountIdsAvailableForSingle && teamIdForSingle
                  ? () => {
                      const [tenant, realmId] = groupKeys[0].split('|');
                      const tenantId = tenantOrder.find(
                        (tid) => tenantsHash[tid]?.tenantName === tenant,
                      );
                      const tenantObj = tenantId ? tenantsHash[tenantId] : undefined;
                      const realmObj = getSandbox(realmId);
                      if (realmObj) handleSelectGlobalRailsSandbox({ sandbox: realmObj });
                      if (tenantObj) {
                        handleSelectGlobalTenant({ tenant: tenantObj });
                        try {
                          switchTenant({ tenant: tenantObj.tenantName });
                        } catch {
                          // Silently ignore tenant switch errors
                        }
                      }
                      openSetUsersTemporaryPasswordModal({ accountIds, teamId: teamIdForSingle });
                    }
                  : undefined,
              disabled: !(isSingleTeam && allAccountIdsAvailableForSingle && teamIdForSingle),
            },
          ],
        },
        {
          title: 'Account Management',
          items: [
            {
              label: 'Mark as CSM Account',
              onClick:
                accountIds.length === 1
                  ? () =>
                      openModal({
                        modal: ({ toggle }) => (
                          <SimpleModal
                            open
                            onClose={toggle}
                            onConfirm={async () => {
                              if (!token) return;
                              try {
                                const [tenant, realmId] = groupKeys[0].split('|');
                                const headers = ApiClientManager.buildUrlHeaders({
                                  tenantName: tenant,
                                  realmId,
                                });
                                await updateAccountAttributesApi({
                                  params: { account_id: accountIds[0], is_csm: true },
                                  token,
                                  config: { headers },
                                });
                                showSuccess('Update account attributes successful!');
                              } catch (e: any) {
                                const apiMessage =
                                  e?.data?.message || e?.response?.data?.message || e?.message;
                                const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '!';
                                showError(`Update account attributes failed${apiMessageSuffix}`);
                              }
                            }}
                            closeOnConfirm
                            Body={<div>Mark this account as a CSM account?</div>}
                          />
                        ),
                      })
                  : undefined,
              disabled: accountIds.length !== 1,
            },
            {
              label: 'Bulk Accounts Activation',
              onClick: accountIds.length
                ? () =>
                    openModal({
                      modal: ({ toggle }) => (
                        <SimpleModal
                          open
                          onClose={toggle}
                          onConfirm={async () => {
                            if (!token) return;
                            const promises = groupKeys.map((key) => {
                              const [tenant, realmId] = key.split('|');
                              const idsForGroup = (groups[key] || [])
                                .map((m) => impersonationMap.get(m.id)?.accountId)
                                .filter(Boolean) as number[];
                              if (idsForGroup.length === 0) return Promise.resolve();
                              return ProfileServices.bulkActivateAccounts(
                                { account_ids: idsForGroup },
                                {
                                  headers: ApiClientManager.buildUrlHeaders({
                                    tenantName: tenant,
                                    realmId,
                                  }),
                                },
                                token,
                              );
                            });
                            await Promise.all(promises);
                          }}
                          closeOnConfirm
                          Body={<div>Activate {accountIds.length} accounts?</div>}
                        />
                      ),
                    })
                : undefined,
              disabled: accountIds.length === 0,
            },
            ...buildSignInStatusItems({
              isOn: isSignInStatusOn,
              isReady: allMembershipIdsAvailable,
              groupKey: groupKeys[0],
              groupMembers: groups[groupKeys[0]],
              selectionSize: members.length,
              impersonationMap,
              batchLimit: signInStatusBatchLimit,
              openModal: openSetSignInStatusModal,
            }),
            {
              label: 'Reassociate MFA',
              onClick:
                isSingleTeam &&
                emails.length > 0 &&
                emails.length === members.length &&
                groups[groupKeys[0]]?.length === members.length
                  ? () =>
                      openModal({
                        modal: ({ toggle }) => (
                          <SimpleModal
                            open
                            onClose={toggle}
                            onConfirm={async () => {
                              if (!token) return;
                              try {
                                const [tenant, realmId] = groupKeys[0].split('|');
                                const headers = ApiClientManager.buildUrlHeaders({
                                  tenantName: tenant,
                                  realmId,
                                });
                                await bulkToggleMfaReset({
                                  params: { emails },
                                  token,
                                  config: { headers },
                                });
                                showSuccess('Reassociate MFA successful!');
                              } catch (e) {
                                showError(`Reassociate MFA failed: ${extractApiErrorMessage(e)}`);
                              }
                            }}
                            closeOnConfirm
                            Body={
                              <div>
                                Reassociate MFA for {emails.length} users? They will be prompted to
                                set up MFA again on next login.
                              </div>
                            }
                          />
                        ),
                      })
                  : undefined,
              disabled:
                !isSingleTeam ||
                emails.length === 0 ||
                emails.length !== members.length ||
                groups[groupKeys[0]]?.length !== members.length,
            },
          ],
        },
        {
          title: 'Role Management',
          items: [
            {
              label: 'Modify Access Level',
              subItems: accessRolesStructure.map(onModifyRoleOption),
            },
            buildChelloTierMenuItem({
              // Chello subscriptions are keyed by account id on one team; a
              // single-team selection always carries a parsed teamId.
              enabled: allAccountIdsAvailableForSingle,
              onSelect: (tier) => {
                const [tenant, realmId] = groupKeys[0].split('|');
                openSetChelloTierModal({
                  tenant,
                  realmId,
                  teamId: teamIdForSingle,
                  accountIds,
                  tier,
                });
              },
            }),
          ],
        },
      ];

      // Set sections and mark pending open - useEffect will open menu after state updates
      pendingOpenRef.current = target;
      setMenuSections(sections);
    },
    [
      accessRolesStructure,
      dispatch,
      openModal,
      openSetUsersTemporaryPasswordModal,
      openSetChelloTierModal,
      openSetSignInStatusModal,
      signInStatusBatchLimit,
      isSignInStatusOn,
      token,
      handleSelectGlobalRailsSandbox,
      handleSelectGlobalTenant,
      getSandbox,
      tenantsHash,
      tenantOrder,
      switchTenant,
      showSuccess,
      showError,
    ],
  );

  return {
    openMembersV2BulkActionMenu,
    MembersV2BulkActionMenuComponent: (
      <ActionMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        open={isOpen}
        onClose={closeMenu}
        sections={menuSections}
        title="Bulk Actions"
      />
    ),
  };
};

export default useMembersV2BulkActionMenu;
